/**
 * Resources Chart Registry
 * 
 * This registry manages chart configurations for platform resource monitoring charts.
 * These charts are technology-agnostic and monitor system-level resources.
 * 
 * Unlike Site charts which use component references, Resource charts use configKey
 * references because they share the same ResourcesChart component with different configs.
 * 
 * @module Performance/Resources
 */

import { ChartGroupConfig } from '../chartGroupConfigs';

/**
 * Resource Chart Configuration
 * Extends ChartGroupConfig to use configKey instead of component
 */
export interface ResourceChartConfig {
  configKey: 'memory' | 'cpu';
  fullscreenKey: string;
  gridSize?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
}

/**
 * Resource Chart Group Configuration
 * Extends ChartGroupConfig with ResourceChartConfig
 */
export interface ResourceChartGroupConfig {
  groupTitle: string;
  charts: ResourceChartConfig[];
}

/**
 * Resources Chart Groups Configuration
 * 
 * Organizes platform resource monitoring charts.
 * Currently includes:
 * - Memory Utilization: Used vs Max memory over time
 * - CPU Utilization: CPU usage percentage over time
 * 
 * These charts use the ResourcesChart component with different configurations
 * specified by configKey (memory or cpu).
 */
export const RESOURCES_CHART_GROUPS: ResourceChartGroupConfig[] = [
  {
    groupTitle: "Resource Monitoring",
    charts: [
      {
        configKey: 'memory',
        fullscreenKey: 'memory',
        // gridSize is optional - defaults to getChartGridSize() calculation
      },
      {
        configKey: 'cpu',
        fullscreenKey: 'cpu',
      },
    ],
  },
];

/**
 * Get Resources Chart Groups
 * 
 * Returns the resource chart group configurations.
 * Since resources are technology-agnostic, no device type parameter is needed.
 * 
 * @returns Array of resource chart group configurations
 * 
 * @example
 * const chartGroups = getResourcesChartGroups();
 * // Returns: [{ groupTitle: "Resource Monitoring", charts: [...] }]
 */
export function getResourcesChartGroups(): ResourceChartGroupConfig[] {
  return RESOURCES_CHART_GROUPS;
}

/**
 * Get Resources Fullscreen Keys
 * 
 * Returns all fullscreen keys for resource charts.
 * Useful for initializing fullscreen state or validation.
 * 
 * @returns Array of fullscreen key strings
 * 
 * @example
 * const keys = getResourcesFullscreenKeys();
 * // Returns: ['memory', 'cpu']
 */
export function getResourcesFullscreenKeys(): string[] {
  const keys: string[] = [];
  
  RESOURCES_CHART_GROUPS.forEach(group => {
    group.charts.forEach(chart => {
      keys.push(chart.fullscreenKey);
    });
  });
  
  return keys;
}

/**
 * Get Resources Chart Count
 * 
 * Returns the total number of resource charts.
 * 
 * @returns Total number of resource charts
 * 
 * @example
 * const count = getResourcesChartCount();  // Returns 2
 */
export function getResourcesChartCount(): number {
  let count = 0;
  
  RESOURCES_CHART_GROUPS.forEach(group => {
    count += group.charts.length;
  });
  
  return count;
}
